import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Badge,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { SCADAAlarm, AlarmFilter, AlarmListResponse } from '../../types';
import { scadaAlarmsAPI } from '../../services/alarmService';

interface AlarmListProps {
  siteId?: number;
  onAlarmSelect?: (alarm: SCADAAlarm) => void;
  showFilters?: boolean;
  maxHeight?: number;
  refreshInterval?: number;
  onlyActive?: boolean;
}

const PRIORITY_COLORS = {
  LOW: 'success',
  MEDIUM: 'warning',
  HIGH: 'error',
  CRITICAL: 'error',
} as const;

const PRIORITY_ICONS = {
  LOW: InfoIcon,
  MEDIUM: WarningIcon,
  HIGH: ErrorIcon,
  CRITICAL: ErrorIcon,
} as const;

const STATE_COLORS = {
  ACTIVE: 'error',
  ACKNOWLEDGED: 'warning',
  CLEARED: 'success',
  SUPPRESSED: 'default',
} as const;

export const AlarmList: React.FC<AlarmListProps> = ({
  siteId,
  onAlarmSelect,
  showFilters = true,
  maxHeight = 600,
  refreshInterval = 30000, // 30 seconds
  onlyActive = false,
}) => {
  const [alarms, setAlarms] = useState<SCADAAlarm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<any>(null);

  // Filter states
  const [filters, setFilters] = useState<AlarmFilter>({});
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [tempFilters, setTempFilters] = useState<AlarmFilter>({});

  // Action dialogs
  const [acknowledgeDialog, setAcknowledgeDialog] = useState<{
    open: boolean;
    alarm: SCADAAlarm | null;
    comment: string;
  }>({ open: false, alarm: null, comment: '' });

  const [clearDialog, setClearDialog] = useState<{
    open: boolean;
    alarm: SCADAAlarm | null;
    clearType: 'local' | 'remote' | 'both';
    comment: string;
  }>({ open: false, alarm: null, clearType: 'local', comment: '' });

  const [suppressDialog, setSuppressDialog] = useState<{
    open: boolean;
    alarm: SCADAAlarm | null;
    reason: string;
    until: Date | null;
  }>({ open: false, alarm: null, reason: '', until: null });

  // Selected alarms for bulk operations
  const [selectedAlarms, setSelectedAlarms] = useState<number[]>([]);

  const fetchAlarms = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        ...filters,
        site: siteId,
        page: page + 1,
        page_size: rowsPerPage,
        ordering: '-triggered_at',
      };
      if (onlyActive) {
        params.state = ['ACTIVE'];
      }

      const response = await scadaAlarmsAPI.getAll(params) as AlarmListResponse;
      setAlarms(response.results);
      setTotalCount(response.count);
      setSummary(response.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch alarms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlarms();
  }, [page, rowsPerPage, filters, siteId]);

  // Auto-refresh
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(fetchAlarms, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, page, rowsPerPage, filters, siteId]);

  const handleAcknowledge = async (alarm: SCADAAlarm, comment: string) => {
    try {
      await scadaAlarmsAPI.acknowledge(alarm.id, {
        alarm_id: alarm.id,
        acknowledgment_comment: comment,
      });
      setAcknowledgeDialog({ open: false, alarm: null, comment: '' });
      fetchAlarms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge alarm');
    }
  };

  const handleClear = async (alarm: SCADAAlarm, clearType: 'local' | 'remote' | 'both', comment: string) => {
    try {
      if (clearType === 'local') {
        await scadaAlarmsAPI.clearLocal(alarm.id, { alarm_id: alarm.id, clear_type: clearType, clear_comment: comment });
      } else if (clearType === 'remote') {
        await scadaAlarmsAPI.clearRemote(alarm.id, { alarm_id: alarm.id, clear_type: clearType, clear_comment: comment });
      } else {
        await scadaAlarmsAPI.clearBoth(alarm.id, { alarm_id: alarm.id, clear_type: clearType, clear_comment: comment });
      }
      setClearDialog({ open: false, alarm: null, clearType: 'local', comment: '' });
      fetchAlarms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear alarm');
    }
  };

  const handleSuppress = async (alarm: SCADAAlarm, reason: string, until: Date | null) => {
    try {
      await scadaAlarmsAPI.suppress(alarm.id, {
        reason,
        suppressed_until: until ? until.toISOString() : null,
      });
      setSuppressDialog({ open: false, alarm: null, reason: '', until: null });
      fetchAlarms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to suppress alarm');
    }
  };

  const handleUnsuppress = async (alarm: SCADAAlarm) => {
    try {
      await scadaAlarmsAPI.unsuppress(alarm.id);
      fetchAlarms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsuppress alarm');
    }
  };

  const handleBulkAcknowledge = async () => {
    if (selectedAlarms.length === 0) return;
    
    try {
      await scadaAlarmsAPI.bulkAcknowledge(selectedAlarms);
      setSelectedAlarms([]);
      fetchAlarms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge alarms');
    }
  };

  const handleBulkClear = async (clearType: 'local' | 'remote' | 'both') => {
    if (selectedAlarms.length === 0) return;
    
    try {
      await scadaAlarmsAPI.bulkClear(selectedAlarms, clearType);
      setSelectedAlarms([]);
      fetchAlarms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear alarms');
    }
  };

  const handleSelectAlarm = (alarmId: number) => {
    setSelectedAlarms(prev => 
      prev.includes(alarmId) 
        ? prev.filter(id => id !== alarmId)
        : [...prev, alarmId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAlarms.length === alarms.length) {
      setSelectedAlarms([]);
    } else {
      setSelectedAlarms(alarms.map(alarm => alarm.id));
    }
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setShowFilterDialog(false);
    setPage(0);
  };

  const clearFilters = () => {
    setTempFilters({});
    setFilters({});
    setPage(0);
  };

  const getPriorityIcon = (priority: string) => {
    const IconComponent = PRIORITY_ICONS[priority as keyof typeof PRIORITY_ICONS] || InfoIcon;
    return <IconComponent fontSize="small" />;
  };

  const formatDuration = (duration: string) => {
    try {
      return formatDistanceToNow(parseISO(duration), { addSuffix: true });
    } catch {
      return duration;
    }
  };

  const activeFiltersCount = Object.keys(filters).filter(key => 
    filters[key as keyof AlarmFilter] !== undefined && 
    filters[key as keyof AlarmFilter] !== null &&
    (Array.isArray(filters[key as keyof AlarmFilter]) 
      ? (filters[key as keyof AlarmFilter] as any[])?.length > 0
      : true)
  ).length;

  return (
    <Box>
      {/* Summary Cards */}
      {summary && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
              <Typography variant="h6" color="error.main">
                {summary.total_active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
              <Typography variant="h6" color="warning.main">
                {summary.total_acknowledged}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Acknowledged
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                {summary.total_cleared}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cleared
              </Typography>
            </Paper>
          </Stack>
        </Box>
      )}

      {/* Toolbar */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showFilters && (
            <Tooltip title="Filters">
              <IconButton 
                onClick={() => setShowFilterDialog(true)}
                color={activeFiltersCount > 0 ? 'primary' : 'default'}
              >
                <Badge badgeContent={activeFiltersCount} color="primary">
                  <FilterListIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          
          {selectedAlarms.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleBulkAcknowledge}
                startIcon={<CheckCircleIcon />}
              >
                Acknowledge ({selectedAlarms.length})
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleBulkClear('local')}
                startIcon={<ClearIcon />}
              >
                Clear Local ({selectedAlarms.length})
              </Button>
            </Box>
          )}
        </Box>

        <IconButton onClick={fetchAlarms} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Alarms Table */}
      <Paper sx={{ maxHeight, overflow: 'auto' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedAlarms.length > 0 && selectedAlarms.length < alarms.length}
                    checked={alarms.length > 0 && selectedAlarms.length === alarms.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Triggered</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alarms.map((alarm) => (
                <TableRow 
                  key={alarm.id}
                  hover
                  selected={selectedAlarms.includes(alarm.id)}
                  onClick={() => onAlarmSelect?.(alarm)}
                  sx={{ cursor: onAlarmSelect ? 'pointer' : 'default' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedAlarms.includes(alarm.id)}
                      onChange={() => handleSelectAlarm(alarm.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getPriorityIcon(alarm.priority)}
                      label={alarm.priority}
                      color={PRIORITY_COLORS[alarm.priority] as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {alarm.tag_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {alarm.tag_description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={alarm.alarm_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {alarm.alarm_value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Setpoint: {alarm.setpoint}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={alarm.state}
                      color={STATE_COLORS[alarm.state] as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(parseISO(alarm.triggered_at), 'MMM dd, HH:mm:ss')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(parseISO(alarm.triggered_at), { addSuffix: true })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDuration(alarm.duration)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {alarm.state === 'ACTIVE' && (
                        <Tooltip title="Acknowledge">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAcknowledgeDialog({ open: true, alarm, comment: '' });
                            }}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {alarm.state !== 'CLEARED' && (
                        <Tooltip title="Clear">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setClearDialog({ open: true, alarm, clearType: 'local', comment: '' });
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {alarm.state !== 'CLEARED' && alarm.state !== 'SUPPRESSED' && (
                        <Tooltip title="Suppress">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSuppressDialog({ open: true, alarm, reason: '', until: null });
                            }}
                          >
                            <VisibilityOffIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {alarm.state === 'SUPPRESSED' && (
                        <Tooltip title="Unsuppress">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnsuppress(alarm);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAlarmSelect?.(alarm);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Filter Dialog */}
      <Dialog open={showFilterDialog} onClose={() => setShowFilterDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Filter Alarms</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                multiple
                value={tempFilters.priority || []}
                onChange={(e) => setTempFilters(prev => ({ ...prev, priority: e.target.value as string[] }))}
                label="Priority"
              >
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="CRITICAL">Critical</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                multiple
                value={tempFilters.state || []}
                onChange={(e) => setTempFilters(prev => ({ ...prev, state: e.target.value as string[] }))}
                label="State"
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="ACKNOWLEDGED">Acknowledged</MenuItem>
                <MenuItem value="CLEARED">Cleared</MenuItem>
                <MenuItem value="SUPPRESSED">Suppressed</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Alarm Type</InputLabel>
              <Select
                multiple
                value={tempFilters.alarm_type || []}
                onChange={(e) => setTempFilters(prev => ({ ...prev, alarm_type: e.target.value as string[] }))}
                label="Alarm Type"
              >
                <MenuItem value="value_high">Value High</MenuItem>
                <MenuItem value="value_low">Value Low</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
                <MenuItem value="communication_failure">Communication Failure</MenuItem>
                <MenuItem value="quality_bad">Quality Bad</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Date From"
              type="datetime-local"
              value={tempFilters.date_from || ''}
              onChange={(e) => setTempFilters(prev => ({ ...prev, date_from: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Date To"
              type="datetime-local"
              value={tempFilters.date_to || ''}
              onChange={(e) => setTempFilters(prev => ({ ...prev, date_to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setShowFilterDialog(false)}>Cancel</Button>
          <Button onClick={applyFilters} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>

      {/* Acknowledge Dialog */}
      <Dialog open={acknowledgeDialog.open} onClose={() => setAcknowledgeDialog({ open: false, alarm: null, comment: '' })}>
        <DialogTitle>Acknowledge Alarm</DialogTitle>
        <DialogContent>
          {acknowledgeDialog.alarm && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Tag: {acknowledgeDialog.alarm.tag_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {acknowledgeDialog.alarm.alarm_type}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Acknowledgment Comment (optional)"
            value={acknowledgeDialog.comment}
            onChange={(e) => setAcknowledgeDialog(prev => ({ ...prev, comment: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcknowledgeDialog({ open: false, alarm: null, comment: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={() => acknowledgeDialog.alarm && handleAcknowledge(acknowledgeDialog.alarm, acknowledgeDialog.comment)}
            variant="contained"
          >
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Dialog */}
      <Dialog open={clearDialog.open} onClose={() => setClearDialog({ open: false, alarm: null, clearType: 'local', comment: '' })}>
        <DialogTitle>Clear Alarm</DialogTitle>
        <DialogContent>
          {clearDialog.alarm && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Tag: {clearDialog.alarm.tag_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {clearDialog.alarm.alarm_type}
              </Typography>
            </Box>
          )}
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Clear Type</InputLabel>
            <Select
              value={clearDialog.clearType}
              onChange={(e) => setClearDialog(prev => ({ ...prev, clearType: e.target.value as 'local' | 'remote' | 'both' }))}
              label="Clear Type"
            >
              <MenuItem value="local">Local Only</MenuItem>
              <MenuItem value="remote">Remote Only</MenuItem>
              <MenuItem value="both">Both Local and Remote</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Clear Comment (optional)"
            value={clearDialog.comment}
            onChange={(e) => setClearDialog(prev => ({ ...prev, comment: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialog({ open: false, alarm: null, clearType: 'local', comment: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={() => clearDialog.alarm && handleClear(clearDialog.alarm, clearDialog.clearType, clearDialog.comment)}
            variant="contained"
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suppress Dialog */}
      <Dialog open={suppressDialog.open} onClose={() => setSuppressDialog({ open: false, alarm: null, reason: '', until: null })}>
        <DialogTitle>Suppress Alarm</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={2}
            value={suppressDialog.reason}
            onChange={(e) => setSuppressDialog(prev => ({ ...prev, reason: e.target.value }))}
            sx={{ mt: 1 }}
          />
          <Box sx={{ mt: 2 }}>
            <DateTimePicker
              label="Suppress Until (optional)"
              value={suppressDialog.until}
              onChange={(date) => setSuppressDialog(prev => ({ ...prev, until: date }))}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuppressDialog({ open: false, alarm: null, reason: '', until: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (suppressDialog.alarm) {
                handleSuppress(suppressDialog.alarm, suppressDialog.reason, suppressDialog.until);
              }
            }}
          >
            Suppress
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 